import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AlarmsService} from '../../../../core/services/alarms.service';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../app/app.state';
import {TileIdMap} from '../../../../shared/models/tile';
import {TilesService} from '../../../../core/services/tiles.service';
import {AlarmEvent, EventQueryResult} from '../../../../shared/models/alarm';
import {SortTable, SortTableRow} from '../../../../shared/models/sort-table/types';
import {TitleBreadcrumbsChangedAction, TitleChangedAction} from '../../../../core/actions/title.actions';
import {UrlService} from '../../../../core/services/url.service';
import {SortTableCellButtons, SortTableCellKebabMenu, SortTableCellTextPill} from '../../../../shared/models/sort-table/cell-classes';
import {UtilsService} from '../../../../core/services/utils.service';
import {PillColor, PillLetter} from '../../../../shared/models/pill';
import {Option} from '../../../../shared/models/option';
import {BaseTableViewComponent} from '../../../components/_base-table-view/base-table-view.component';
import {forkJoin, Subscription} from 'rxjs';
import {ObjectModel, ObjectType} from '../../../../shared/models/object.model';
import {DevicesService} from '../../../../core/services/devices.service';
import {HubsService} from '../../../../core/services/hubs.service';
import {SpinnerComponent} from '../../../components/spinner/spinner.component';
import {SortTableComponent} from '../../../components/sort-table/sort-table.component';

const COL_IDX_KEBAB = 0;
const COL_IDX_OBJECT = 1;
const COL_IDX_STATUS = 2;
const COL_IDX_BUTTON = 3;
const COL_IDX_TIME = 4;

@Component({
  selector: 'motn-alarms-history',
  templateUrl: './alarms-history.component.html',
  styleUrls: ['./alarms-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SpinnerComponent,
    SortTableComponent,
  ],
})
export class AlarmsHistoryComponent extends BaseTableViewComponent implements OnInit, OnDestroy {
  page: number = 1;
  rowsPerPage: number = 25;
  events: EventQueryResult;

  tiles: TileIdMap = {};

  private pageNumberSub: Subscription;


  constructor(
    private store: Store<AppState>,
    private alarmsService: AlarmsService,
    private tilesService: TilesService,
    private devicesService: DevicesService,
    private hubsService: HubsService,
    private urlService: UrlService,
  ) {
    super();
  }


  ngOnInit(): void {
    this.updateTitle();

    forkJoin([
      this.tilesService.getUserTiles$(),
      this.hubsService.getUserHubs$(),
      this.devicesService.getUserDevices$(),
    ]).subscribe({
      next: () => {
        this.pageNumberSub = this.urlService.pageNumber$.subscribe({
          next: (page) => {
            this.pageNumberChanged(page);
          },
        });
      },
    });
  }


  ngOnDestroy() {
    this.pageNumberSub?.unsubscribe();
  }


  pageNumberChanged(page: number): void {
    this.page = page;
    this.fetchPageData();
  }


  private fetchPageData(): void {
    this.alarmsService.fetchAlarmEvents$({
      ObjectType: 'tile,hub,device',
      Qty: this.rowsPerPage + '',
      Page: this.page + '',
    }).subscribe({
      next: (data) => {
        this.events = data;
        this.updateTable();
      },
    });
  }


  protected updateTable() {
    const colKebabWidth = 40;
    const colSubjectWidth = 250;
    const colStatusWidth = 120;
    const colTimeWidth = 260;
    const colButtonWidth = 120;

    const table: SortTable = {
      css: {
        'min-width': (colKebabWidth + colSubjectWidth + colTimeWidth + colStatusWidth + colButtonWidth) + 'px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: this.rowsPerPage,
        startOnPage: this.page,
        maxPageOptions: 12,
        totalRowQty: this.events.Count,
      },
    };

    {
      table.columns[COL_IDX_KEBAB] = {
        name: '',
        css: {
          'width': colKebabWidth + 'px',
        },
      };

      table.columns[COL_IDX_OBJECT] = {
        name: 'Subject',
        css: {
          'width': `calc(100% - ${colKebabWidth + colTimeWidth + colStatusWidth + colButtonWidth}px)`,
          'font-weight': '600',
        },
      };

      table.columns[COL_IDX_STATUS] = {
        name: 'Status',
        css: {
          'width': colStatusWidth + 'px',
          'justify-content': 'center',
        },
      };

      table.columns[COL_IDX_TIME] = {
        name: 'Last Status Change',
        css: {
          'width': colTimeWidth + 'px',
          'justify-content': 'flex-end',
        },
      };

      table.columns[COL_IDX_BUTTON] = {
        name: '',
        css: {
          'width': colButtonWidth + 'px',
          'justify-content': 'center',
        },
      };
    }

    for (let event of this.events.ResultSet) {
      const row: SortTableRow = {
        cells: [],
        context: event.UUID,
      };

      this.upsertRowData(row, event);
      table.rows.push(row);
    }

    this.table$.next(table);
  }


  protected upsertRowData(row: SortTableRow, event: AlarmEvent) {
    let object: ObjectModel;

    switch (event.ObjectType) {
      case ObjectType.TILE:
        const tile = this.tilesService.sessUserTiles[event.ObjectID];
        object = ObjectModel.fromTile(tile);
        break;
      case ObjectType.DEVICE:
        const device = this.devicesService.sessUserDevices[event.ObjectID];
        object = ObjectModel.fromDevice(device);
        break;
      case ObjectType.HUB:
        const hub = this.hubsService.sessUserHubs[event.ObjectID];
        object = ObjectModel.fromHub(hub);
        break;
      default:
        throw new Error('unsupported ObjectType');
    }

    const detailsUrl = UrlService.makeAlarmEventDetailsUrl(event.UUID);
    const kebabOptions: Option[] = [
      {
        id: 'dt',
        name: `View Details`,
        href: detailsUrl,
      },
    ];

    let status: string;
    let time: string;
    let color: PillColor;

    if (event.ClearTime) {
      status = 'Cleared';
      time = UtilsService.makeMonospaceTime(event.ClearTime);
      color = 'green';
    } else if (event.AckTime) {
      status = 'Acknowledged';
      time = UtilsService.makeMonospaceTime(event.AckTime);
      color = 'yellow';
    } else {
      status = 'Unacknowledged';
      time = UtilsService.makeMonospaceTime(event.StartTime);
      color = 'red';
    }

    // Make the entire row clickable by setting href
    row.href = detailsUrl;

    row.cells[COL_IDX_KEBAB] = new SortTableCellKebabMenu(object?.id, kebabOptions, object?.name, false, 1);

    const cell = new SortTableCellTextPill(object?.name, object?.id)
      .setPillLetter(<PillLetter>event.ObjectType.substr(0, 1))
      .setShowPillFirst(true);

    // For tiles, add display name if available
    if (event.ObjectType === ObjectType.TILE) {
      const tile = this.tilesService.sessUserTiles[event.ObjectID];
      cell.displayName = tile?.Appearance?.DisplayName;
    }

    row.cells[COL_IDX_OBJECT] = cell;

    row.cells[COL_IDX_STATUS] = new SortTableCellTextPill('', status).setPillColor(color).setPillFill('solid');
    row.cells[COL_IDX_BUTTON] = new SortTableCellButtons([
      {
        id: event.UUID,
        text: 'View Details',
        href: detailsUrl,
        colors: ['indigo'],
      },
    ]);
    row.cells[COL_IDX_TIME] = new SortTableCellTextPill(time, '', true);
  }


  /**
   *
   * @private
   */
  private updateTitle() {
    this.store.dispatch(new TitleChangedAction(
      '',
      'Alarm History',
    ));

    this.store.dispatch(new TitleBreadcrumbsChangedAction(
      [
        {
          text: 'Alarms',
        },
        {
          text: 'History',
          path: UrlService.makeAlarmsHistoryUrl(),
        },
      ],
    ));
  }
}
